import { z } from 'zod'
import * as R from 'remeda'
import type { Street } from '@/types/api/reports'
import { t } from '@/locales/i18n'

export type MainAction = 'ANY' | 'BET' | 'FOLD' | 'CALL' | 'CHECK'
export type BetAction = 'SMALL' | 'MEDIUM' | 'LARGE' | 'OVERBET'
export type SelectedActions = (MainAction | BetAction)[]
export type ActionSequence = SelectedActions[]

const mainFirstActionsLiteral = ['ANY', 'CHECK', 'BET'] as const
const mainNextActionsLiteral = ['ANY', 'FOLD', 'CHECK', 'CALL', 'BET'] as const
const betActionsLiteral = ['SMALL', 'MEDIUM', 'LARGE', 'OVERBET'] as const

export const mainFirstActions: MainAction[] = [...mainFirstActionsLiteral]
export const mainNextActions: MainAction[] = [...mainNextActionsLiteral]
export const betActions: BetAction[] = [...betActionsLiteral]

const mainFirstEnum = z.enum(mainFirstActionsLiteral)
const mainNextEnum = z.enum(mainNextActionsLiteral)
const betEnum = z.enum(betActionsLiteral)

const validateSlot = (actions: SelectedActions, isFirstSlot: boolean): boolean => {
  const mainEnum = isFirstSlot ? mainFirstEnum : mainNextEnum
  const mainCount = actions.filter(a => mainEnum.safeParse(a).success).length

  // Only 1 main action is allowed
  if (mainCount > 1) return false
  if (mainCount === 1) return actions.length === 1

  // Otherwise everything must be bet actions
  return actions.every(a => betEnum.safeParse(a).success)
}

export const strictActionSequenceSchema = z
  .array(z.array(z.string()).nonempty())
  .min(1)
  .refine(sequence =>
    sequence.every((actions, i) => validateSlot(actions as SelectedActions, i === 0)),
  )

export const displayAsRaise = (sequence: ActionSequence, index: number) =>
  R.pipe(
    sequence,
    R.take(index),
    R.find(s => ['BET', ...betActions].some(a => s?.includes(a as BetAction | 'BET'))),
  ) !== undefined

export const getSequenceLength = (sequence: ActionSequence) =>
  sequence.filter(i => i?.length).length

export const getActionsInSequenceLength = (sequence: ActionSequence) =>
  sequence.reduce((total, subarray) => total + (subarray?.length || 0), 0)

export const getStreet = (filterField: string) => R.first(filterField.split('_')) as Street

export const displaySelectedSequence = (sequence: ActionSequence): string => {
  if (getActionsInSequenceLength(sequence) > 9) return getSequenceLength(sequence).toString()
  return sequence
    .map((s, i) =>
      s
        .map(a =>
          t(`hhv3.filter.actions_sequence.selected_items.${a}`, {
            bet_type: displayAsRaise(sequence, i) ? 'R' : 'B',
          }),
        )
        .join('/'),
    )
    .join('-')
}
